/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// ESTIMATE REVISIONS HOOKS
// ============================================================================

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { QUERY_STALE_TIME } from "@/lib/constants/query-config";
import type { Database } from "@/lib/supabase/types";
import { logger } from "@/lib/utils/logger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

// ============================================================================
// TYPES
// ============================================================================

type EstimateRow = Database["public"]["Tables"]["estimates"]["Row"];

export interface EstimateRevision extends EstimateRow {
  // Additional metadata can be added here
}

export interface EstimateRevisionsResponse {
  revisions: EstimateRevision[];
  rootEstimateId: string;
  currentEstimateId: string;
}

export interface CreateRevisionParams {
  revision_reason: string;
  copy_line_items?: boolean;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to fetch estimate revision history
 */
export function useEstimateRevisions(estimateId: string | null | undefined) {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled: Boolean(estimateId),
    debugKey: "useEstimateRevisions",
  });

  const api = useCallback(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  return useQuery<EstimateRevisionsResponse>({
    queryKey: ["estimate-revisions", estimateId],
    queryFn: async () => {
      if (!estimateId) {
        throw new Error("Estimate ID is required");
      }

      const client = api();
      const response = await client.get<EstimateRevisionsResponse>(
        `/api/estimates/${estimateId}/revisions`,
      );

      if (isApiFailure(response)) {
        throw new Error(response.message || "Failed to fetch revisions");
      }

      return response.data;
    },
    enabled: queryEnabled && Boolean(estimateId),
    staleTime: QUERY_STALE_TIME.STANDARD,
  });
}

/**
 * Hook to create a new estimate revision
 */
export function useCreateEstimateRevision() {
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled: organizationReady } =
    useOrganizationQueryGuard({
      enabled: true,
      debugKey: "useCreateEstimateRevision",
    });
  const queryClient = useQueryClient();

  const api = useCallback(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  return useMutation<
    EstimateRevision,
    Error,
    { estimateId: string; params: CreateRevisionParams }
  >({
    mutationFn: async ({ estimateId, params }) => {
      if (!organizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const client = api();
      const response = await client.post<EstimateRevision>(
        `/api/estimates/${estimateId}/revisions`,
        params,
      );

      if (isApiFailure(response)) {
        throw new Error(response.message || "Failed to create revision");
      }

      return response.data;
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["estimate-revisions", variables.estimateId],
      });
      queryClient.invalidateQueries({
        queryKey: ["estimate", variables.estimateId],
      });
      if (_data?.estimate_id) {
        queryClient.invalidateQueries({
          queryKey: ["estimate", _data.estimate_id],
        });
      }
    },
    onError: (error, variables) => {
      logger.error("Failed to create estimate revision", {
        error: error.message,
        estimateId: variables.estimateId,
      });
    },
  });
}
